import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Check, ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { getPublicPlans, subscribe, type SubscriptionPlan } from "../../services/subscriptionService";
import { useAuth } from "../../hooks/useAuth";

const CARDS_VISIBLE = 3;

const HospitalSubscriptionPage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getPublicPlans();
        const list = Array.isArray(data) ? data : [];
        setPlans(list);
        const popular = list.find((p) => p.planTag === "Most Popular");
        setSelectedPlanId(popular?.id ?? list[0]?.id ?? null);
      } catch {
        toast.error("Failed to load plans");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const maxSlide = Math.max(0, plans.length - CARDS_VISIBLE);
  const visiblePlans = plans.slice(slideIndex, slideIndex + CARDS_VISIBLE);
  const showNav = plans.length > CARDS_VISIBLE;

  const [subscribingId, setSubscribingId] = useState<string | null>(null);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    try {
      setSubscribingId(plan.id);

      const customToken = localStorage.getItem("temp_subscription_token") || undefined;

      const res = await subscribe(plan.id, customToken);

      if (res.data?.success && res.data?.tenantUrl) {
        localStorage.removeItem("temp_subscription_token");
        toast.success(`Successfully subscribed to ${plan.planName}! Redirecting...`, { duration: 5000 });

        setTimeout(() => {
          window.location.href = res.data.tenantUrl;
        }, 3000);
      } else {
        toast.error("Subscription succeeded, but no tenant URL was returned.");
        navigate("/login");
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to subscribe");
    } finally {
      setSubscribingId(null);
    }
  };

  const PlanCard = ({
    plan,
    isSelected,
    onSelect,
    onSubscribe,
    loading,
  }: {
    plan: SubscriptionPlan;
    isSelected: boolean;
    onSelect: () => void;
    onSubscribe: () => void;
    loading: boolean;
  }) => {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect()}
        className={`
          relative flex flex-col h-full w-full max-w-[480px] mx-auto
          rounded-2xl border px-6 py-6 cursor-pointer
          transition-all duration-300 transform-gpu gap-4
          ${isSelected
            ? "bg-gray-900 text-white border-gray-900 shadow-2xl scale-105 z-10"
            : "bg-white border-gray-200 shadow-lg hover:shadow-2xl hover:-translate-y-1 hover:border-primary-500 hover:bg-primary-50"
          }
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isSelected ? "bg-primary-500/20" : "bg-gray-100"
              }`}
          >
            <Check
              size={18}
              strokeWidth={2.5}
              className={
                isSelected ? "text-primary-300" : "text-primary-600"
              }
            />
          </div>

          {plan.planTag ? (
            <span
              className={`inline-flex max-w-[60%] px-3 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider truncate overflow-hidden whitespace-nowrap ${isSelected
                ? "bg-primary-500/20 text-primary-300"
                : "bg-gray-100 text-gray-600"
                }`}
            >
              {plan.planTag}
            </span>
          ) : null}
        </div>

        {/* Plan Name */}
        <h2
          className={`text-xl font-semibold text-center uppercase tracking-wide truncate overflow-hidden whitespace-nowrap ${isSelected ? "text-white" : "text-gray-900"
            }`}
        >
          {plan.planName}
        </h2>

        {/* Price */}
        <div className="flex items-baseline gap-2 flex-shrink-0">
          <p
            className={`text-2xl font-bold ${isSelected ? "text-white" : "text-gray-900"
              }`}
          >
            {plan.currency === "USD" && "$"}
            {plan.price}
            {plan.currency !== "USD" && ` ${plan.currency}`}
          </p>

          <p
            className={`text-xs ${isSelected ? "text-gray-400" : "text-gray-500"
              }`}
          >
            /{plan.durationValue} {plan.durationType}
          </p>
        </div>

        {/* Divider */}
        <div
          className={`w-full border-t ${isSelected ? "border-gray-700" : "border-gray-200"
            }`}
        />

        {/* Features */}
        {plan.includedFeatures && plan.includedFeatures.length > 0 ? (
          <ul className="flex-1 min-h-0 overflow-y-auto max-h-[160px] pr-1 space-y-2 text-sm">
            {plan.includedFeatures.map((f, i) => (
              <li key={i} className="flex items-center gap-3">
                <span
                  className={`shrink-0 ${isSelected ? "text-primary-400" : "text-primary-600"
                    }`}
                >
                  <Check size={16} strokeWidth={2.5} />
                </span>
                <span
                  className={`truncate overflow-hidden whitespace-nowrap ${isSelected ? "text-gray-300" : "text-gray-600"
                    }`}
                >
                  {f}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex-1 min-h-0" />
        )}

        {/* Subscribe Button */}
        <div className="flex-shrink-0 mt-auto">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSubscribe();
            }}
            disabled={loading}
            className="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:opacity-90 disabled:opacity-50 transition"
          >
            {loading ? "Subscribing..." : "Subscribe"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-50">
      {/* Top Header bar with logout */}
      <div className="flex justify-between items-center bg-white px-8 py-4 border-b border-gray-100 flex-shrink-0">
        <h1 className="text-xl font-bold tracking-tight text-primary-600">DiagnomIQ</h1>
        <button
          onClick={logout}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
        >
          <LogOut size={16} />
          <span>Sign Out</span>
        </button>
      </div>

      <div className="flex-1 flex flex-col w-full max-w-[1700px] mx-auto px-8 lg:px-14 py-6 min-h-0 overflow-hidden justify-center">
        <div className="text-center mb-6">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
            Subscription Plans
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Choose a plan that fits your needs.
          </p>
        </div>

        {loading ? (
          <div className="flex-1 flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : (
          <div className="relative flex items-center justify-center gap-4 flex-1 min-h-0">
            {showNav && (
              <button
                type="button"
                onClick={() => setSlideIndex((i) => Math.max(0, i - 1))}
              >
                <ChevronLeft size={22} />
              </button>
            )}

            <div className="grid grid-cols-1 md:grid-cols-[minmax(320px,1fr)_minmax(320px,1fr)_minmax(320px,1fr)] gap-8 items-stretch justify-center flex-1 min-w-0 h-full">
              {visiblePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isSelected={selectedPlanId === plan.id}
                  onSelect={() => setSelectedPlanId(plan.id)}
                  onSubscribe={() => handleSubscribe(plan)}
                  loading={subscribingId === plan.id}
                />
              ))}
            </div>

            {showNav && (
              <button
                type="button"
                onClick={() => setSlideIndex((i) => Math.min(maxSlide, i + 1))}
              >
                <ChevronRight size={22} />
              </button>
            )}
          </div>
        )}

        {!loading && plans.length === 0 && (
          <p className="text-center text-gray-500 py-12">
            No plans available at the moment.
          </p>
        )}
      </div>
    </div>
  );
};

export default HospitalSubscriptionPage;
